import { useScrollReveal } from '../hooks/useScrollReveal';

const knowledgeBases = [
  {
    label: '评价标准库',
    desc: '观察维度与判断规则，定义思政课评课框架',
    icon: 'ri-list-check-3',
    items: ['5维度', '17指标', '观测点', '判断规则'],
    color: 'primary' as const,
  },
  {
    label: '教材理论库',
    desc: '章节结构、教学重难点与核心目标',
    icon: 'ri-book-open-line',
    items: ['章节图谱', '重难点', '核心目标'],
    color: 'accent' as const,
  },
  {
    label: '思政术语库',
    desc: '核心概念关系与同义表达归一',
    icon: 'ri-bookmark-line',
    items: ['概念体系', '同义归一', '关系图谱'],
    color: 'primary' as const,
  },
  {
    label: '权威资料库',
    desc: '政策文件、官方数据与重大工程',
    icon: 'ri-folder-shield-line',
    items: ['政策文件', '官方数据', '重大工程'],
    color: 'accent' as const,
  },
];

const graphLayers = [
  {
    num: '01',
    label: '基础资料层',
    desc: '教材章节、核心概念、权威论述、典型案例',
    icon: 'ri-database-2-line',
    color: 'primary' as const,
  },
  {
    num: '02',
    label: '概念与理论关系',
    desc: '概念层级、理论命题、教学目标关联',
    icon: 'ri-node-tree',
    color: 'accent' as const,
  },
  {
    num: '03',
    label: '课堂理解与评价',
    desc: '教学逻辑图谱、专题咬合、证据映射',
    icon: 'ri-brain-line',
    color: 'primary' as const,
  },
];

const colorMap = {
  primary: {
    bar: 'bg-primary-500',
    iconBg: 'bg-primary-50 border-primary-200/50',
    iconText: 'text-primary-600',
    badgeBg: 'bg-primary-100/60 border-primary-200/30',
    badgeText: 'text-primary-700',
    cardBg: 'glass-liquid',
  },
  accent: {
    bar: 'bg-accent-500',
    iconBg: 'bg-accent-50 border-accent-200/50',
    iconText: 'text-accent-600',
    badgeBg: 'bg-accent-100/60 border-accent-200/30',
    badgeText: 'text-accent-700',
    cardBg: 'glass-liquid',
  },
};

export default function P31KnowledgeBaseNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden overflow-y-auto">
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-10 lg:px-16 flex flex-col py-12 md:py-16 lg:py-20 gap-8 md:gap-10">
        {/* 标题 */}
        <div ref={titleRef} className="text-center">
          <span className={`inline-flex items-center gap-2 text-base font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />
            知识底座
          </span>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            知识库与学科知识图谱
          </h2>
          <p className={`text-base md:text-lg text-foreground-950 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            多模态让系统看见课堂，知识库让系统知道应该怎样理解课堂
          </p>
        </div>

        {/* 第一层：四大知识库 — 2×2大卡片 */}
        <div className={`transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center gap-3 mb-3 md:mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-background-300/50 to-transparent" />
            <span className="text-lg font-body font-semibold text-foreground-950 uppercase tracking-wider">四大知识库</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-background-300/50 to-transparent" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {knowledgeBases.map((kb, i) => {
              const c = colorMap[kb.color];
              return (
                <div
                  key={kb.label}
                  className={`relative rounded-xl p-6 md:p-8 ${c.cardBg}`}
                  style={{ transitionDelay: `${350 + i * 100}ms` }}
                >
                  <div className={`absolute top-0 left-4 right-4 h-1 rounded-b-md ${c.bar}`} />
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-2xl ${c.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${kb.icon} text-3xl ${c.iconText}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading text-3xl font-bold text-foreground-950 mb-2">{kb.label}</h4>
                      <p className="text-base text-foreground-950 font-body leading-relaxed">{kb.desc}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {kb.items.map((item) => (
                      <span key={item} className={`px-3 py-1.5 rounded-lg text-base font-body font-medium ${c.badgeBg} ${c.badgeText} border`}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 箭头过渡 */}
        <div className={`flex items-center justify-center transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col items-center">
            <span className="text-base font-body text-accent-600 font-semibold mb-1">汇聚构建</span>
            <div className="w-10 h-10 rounded-full glass-gold border-accent-200/40 flex items-center justify-center">
              <i className="ri-arrow-down-line text-lg text-accent-600" />
            </div>
          </div>
        </div>

        {/* 第二层：三层知识图谱 */}
        <div className={`transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center gap-3 mb-3 md:mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-background-300/50 to-transparent" />
            <span className="text-lg font-body font-semibold text-foreground-950 uppercase tracking-wider">学科知识图谱</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-background-300/50 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {graphLayers.map((layer, i) => {
              const c = colorMap[layer.color];
              return (
                <div
                  key={layer.label}
                  className={`relative rounded-xl p-4 md:p-5 border transition-all duration-500 ${c.cardBg}`}
                  style={{ transitionDelay: `${750 + i * 120}ms` }}
                >
                  <div className={`absolute top-0 left-0 w-8 h-1 rounded-br-md ${c.bar}`} />

                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-8 h-8 rounded-lg ${c.badgeBg} ${c.badgeText} border text-sm font-mono font-bold flex items-center justify-center`}>
                      {layer.num}
                    </span>
                    <div className={`w-9 h-9 rounded-lg ${c.iconBg} flex items-center justify-center`}>
                      <i className={`${layer.icon} text-base ${c.iconText}`} />
                    </div>
                  </div>

                  <h4 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">{layer.label}</h4>
                  <p className="text-base text-foreground-950 font-body leading-relaxed">{layer.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 箭头过渡 */}
        <div className={`flex items-center justify-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col items-center">
            <span className="text-base font-body text-foreground-950 mb-1">融合应用</span>
            <div className="w-10 h-10 rounded-full bg-primary-50 border border-primary-200/40 flex items-center justify-center">
              <i className="ri-arrow-down-line text-lg text-primary-500" />
            </div>
          </div>
        </div>

        {/* 第三层：总结 — 双图谱 */}
        <div className={`transition-all duration-1000 delay-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="rounded-2xl glass-liquid p-5 md:p-6">
            <div className="flex items-start gap-3 md:gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-accent-50 border border-accent-200/40 flex items-center justify-center flex-shrink-0">
                <i className="ri-git-branch-line text-xl text-accent-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  学科知识图谱 + 教学逻辑图谱
                </h3>
                <p className="text-base md:text-lg text-foreground-950 font-body leading-relaxed">
                  学科知识图谱提供课程标准和概念关系，教学逻辑图谱呈现具体课堂的实际组织方式。
                  两者结合，系统既能判断"教师讲了什么"，也能判断"这些内容是否构成了这门课应该完成的理论结构"。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 p-4 rounded-xl glass-liquid">
                <div className="w-8 h-8 rounded-lg bg-primary-100 border border-primary-200/40 flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-2-line text-sm text-primary-600" />
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-foreground-950 mb-1">学科知识图谱</p>
                  <p className="text-base text-foreground-950 font-body">课程标准、概念体系、理论框架</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl glass-liquid-gold border-accent-200/30">
                <div className="w-8 h-8 rounded-lg glass-gold border-accent-200/40 flex items-center justify-center flex-shrink-0">
                  <i className="ri-presentation-line text-sm text-accent-600" />
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-foreground-950 mb-1">教学逻辑图谱</p>
                  <p className="text-base text-foreground-950 font-body">课堂组织、专题咬合、证据映射</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}